import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Phone, Calendar, Bot, Clock, Shield, Sparkles, ArrowRight, Check, Mic, Grid3x3, Volume2, PhoneOff, MessageSquare, Users, Scissors, Flower2, Car, Scale, HeartPulse, Home, Star, Play, SprayCan } from "lucide-react";

const INDUSTRIES = [
  { icon: Scissors, label: "Barbershops", img: "https://images.unsplash.com/photo-1647140655214-e4a2d914971f?crop=entropy&cs=srgb&fm=jpg&w=640&q=75" },
  { icon: Flower2, label: "Salons & Spas", img: "https://images.pexels.com/photos/8834018/pexels-photo-8834018.jpeg?auto=compress&cs=tinysrgb&w=640" },
  { icon: Scale, label: "Law Firms", img: "https://images.pexels.com/photos/8111815/pexels-photo-8111815.jpeg?auto=compress&cs=tinysrgb&w=640" },
  { icon: Car, label: "Auto Detailing", img: "https://images.unsplash.com/photo-1485291571150-772bcfc10da5?crop=entropy&cs=srgb&fm=jpg&w=640&q=75" },
  { icon: HeartPulse, label: "Medical & Wellness", img: "https://images.pexels.com/photos/7800666/pexels-photo-7800666.jpeg?auto=compress&cs=tinysrgb&w=640" },
  { icon: Home, label: "Home Services", img: "https://images.unsplash.com/photo-1676210134188-4c05dd172f89?crop=entropy&cs=srgb&fm=jpg&w=640&q=75" },
  { icon: SprayCan, label: "Cleaning Services", img: "https://images.unsplash.com/photo-1740657254989-42fe9c3b8cce?crop=entropy&cs=srgb&fm=jpg&w=640&q=75" },
];

const WAVEFORM = [0.5, 0.9, 0.4, 1, 0.6, 0.85, 0.45, 0.95, 0.55, 0.8, 0.5, 1, 0.4, 0.9, 0.6, 0.75, 0.5, 0.95, 0.45, 0.85, 0.55];

const DEMO_SCRIPTS = {
  barbershop: { biz: "Northside Barbers", lines: [
    { ai: true, text: "Thanks for calling Northside Barbers, this is Ava. How can I help you today?" },
    { ai: false, text: "Hi, do you have anything tomorrow afternoon?" },
    { ai: true, text: "We do! I have 2:00 PM or 4:30 PM with Mike. Which works better for you?" },
    { ai: false, text: "4:30 works." },
    { ai: true, text: "You're booked for a haircut tomorrow at 4:30 PM. I've just texted you the confirmation." },
  ]},
  cleaning: { biz: "Sparkle Home Cleaning", lines: [
    { ai: true, text: "Welcome to Sparkle Home Cleaning, this is Ava. How can we help you today?" },
    { ai: false, text: "How much for a deep clean of a 2,000 square foot house?" },
    { ai: true, text: "For 2,000 square feet, a deep clean is $280. Is that one-time or are you thinking recurring?" },
    { ai: false, text: "One-time for now. Can someone come Friday morning?" },
    { ai: true, text: "Friday at 9 AM works. I've booked your deep clean and texted the details — the team will confirm your address shortly." },
  ]},
  salon_spa: { biz: "Northside Salon & Spa", lines: [
    { ai: true, text: "Welcome to Northside Salon & Spa, this is Ava. How can we help you today?" },
    { ai: false, text: "Do you do gel manicures? Any openings this week?" },
    { ai: true, text: "Yes! Gel manicures are $50 and take about an hour with Ruby. I have Thursday at 1 PM or Friday at 11 AM." },
    { ai: false, text: "Friday at 11, please." },
    { ai: true, text: "Done — you're booked with Ruby Friday at 11 AM. Confirmation text is on its way." },
  ]},
  dental: { biz: "Lakeside Dental", lines: [
    { ai: true, text: "Welcome to Lakeside Dental, this is Ava. How can we help you today?" },
    { ai: false, text: "I need a checkup — do you take new patients?" },
    { ai: true, text: "We do! A checkup and cleaning is $120 with Dr. Patel. I have Tuesday at 10 AM or Wednesday at 3 PM." },
    { ai: false, text: "Wednesday at 3 works." },
    { ai: true, text: "You're all set for Wednesday at 3 PM. I'll text you the new-patient form link right now." },
  ]},
  auto_detailing: { biz: "Precision Auto Detail", lines: [
    { ai: true, text: "Welcome to Precision Auto Detail, this is Ava. How can we help you today?" },
    { ai: false, text: "What would a full detail cost for an SUV?" },
    { ai: true, text: "For an SUV, our full detail is $189 — interior shampoo, exterior wash, wax, the works. It takes about 3 hours." },
    { ai: false, text: "Perfect, can I bring it in Saturday?" },
    { ai: true, text: "Saturday at 9 AM is yours. Drop-off confirmed and I've texted you the details." },
  ]},
  law: { biz: "Northside Law", lines: [
    { ai: true, text: "Welcome to Northside Law, this is Ava. How may we help you today?" },
    { ai: false, text: "I need to talk to someone about a real estate closing." },
    { ai: true, text: "Of course. Our real estate consultations are 30 minutes at $150. I can book you with Ms. Parker Thursday at 2 PM." },
    { ai: false, text: "That works." },
    { ai: true, text: "Booked for Thursday at 2 PM. I've texted you a short intake form so your consultation stays focused." },
  ]},
  medical: { biz: "Wellpath Clinic", lines: [
    { ai: true, text: "Welcome to Wellpath Clinic, this is Ava. How can we help you today?" },
    { ai: false, text: "I'd like to book a physio appointment." },
    { ai: true, text: "Sure — initial physiotherapy assessments are 45 minutes. I have Monday at 8:30 AM or Tuesday at 5 PM." },
    { ai: false, text: "Tuesday at 5." },
    { ai: true, text: "You're booked Tuesday at 5 PM. Reminder text goes out the day before — see you then." },
  ]},
  home_services: { biz: "TruFlow Plumbing", lines: [
    { ai: true, text: "Welcome to TruFlow Plumbing, this is Ava. How can we help you today?" },
    { ai: false, text: "My water heater is leaking — how much to fix it?" },
    { ai: true, text: "That's a quote on-site job — a technician confirms pricing after seeing it. I can dispatch someone today at 2 PM. What's the address?" },
    { ai: false, text: "44 Elm Street." },
    { ai: true, text: "A technician is booked for 2 PM today at 44 Elm Street. I've texted you the confirmation." },
  ]},
};
const DEMO_INDUSTRY_CHOICES = [
  { key: "barbershop", label: "Barbershop" },
  { key: "cleaning", label: "Cleaning" },
  { key: "salon_spa", label: "Salon & Spa" },
  { key: "dental", label: "Dental" },
  { key: "auto_detailing", label: "Auto Detailing" },
  { key: "law", label: "Law Firm" },
  { key: "medical", label: "Medical" },
  { key: "home_services", label: "Home Services" },
];

export default function Landing() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [demoIndustry, setDemoIndustry] = useState("barbershop");
  const [showAllIndustries, setShowAllIndustries] = useState(false);
  const audioRef = useRef(null);
  const [runKey, setRunKey] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", business: "", message: "" });
  const [contactBusy, setContactBusy] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const sendContact = async (e) => {
    e.preventDefault();
    setContactBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(contact),
      });
      if (!r.ok) throw new Error("send failed");
      setContactSent(true);
    } catch { setContactSent(false); alert("Couldn't send — please try again or email us directly."); }
    finally { setContactBusy(false); }
  };
  useEffect(() => {
    if (!demoOpen) { setDemoStep(0); audioRef.current?.pause(); return undefined; }
    const lines = DEMO_SCRIPTS[demoIndustry].lines;
    let cancelled = false;
    let timer;
    const play = (i) => {
      if (cancelled || i >= lines.length) return;
      setDemoStep(i + 1);  // line appears as its audio starts
      const a = new Audio(demoIndustry === "barbershop" ? `/demo/line${i + 1}.mp3` : `/demo/${demoIndustry}/line${i + 1}.mp3`);
      audioRef.current = a;
      const next = () => { if (!cancelled) timer = setTimeout(() => play(i + 1), 450); };
      a.onended = next;
      a.onerror = () => { if (!cancelled) timer = setTimeout(() => play(i + 1), 2600); };  // no recording yet → paced text playback
      a.play().catch(next);
    };
    play(0);
    return () => { cancelled = true; clearTimeout(timer); audioRef.current?.pause(); };
  }, [demoOpen, runKey, demoIndustry]);
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#070b16]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center font-bold text-slate-950">A</div>
            <span className="font-heading font-extrabold tracking-tight text-xl text-white">ASFA</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#how" className="hover:text-white transition-colors">How it works</a>
            <a href="#industries" className="hover:text-white transition-colors">Industries</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#reviews" className="hover:text-white transition-colors">Reviews</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-signin-btn" className="text-sm font-medium text-slate-300 hover:text-white transition-colors">Sign in</Link>
            <Link to="/signup"><Button data-testid="landing-cta-btn" className="rounded-xl bg-blue-600 hover:bg-blue-500">Get started <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
        </div>
      </header>

      <section className="relative bg-[#070b16] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(700px 380px at 75% 20%, rgba(47,123,255,0.25), transparent), radial-gradient(500px 300px at 10% 80%, rgba(47,123,255,0.12), transparent)" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-400/30 text-blue-300 text-xs font-semibold tracking-widest uppercase mb-6">
              <Sparkles className="w-3.5 h-3.5" /> AI receptionist for modern businesses
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              Never miss<br /><span className="text-blue-500">a call</span> again.
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
              ASFA answers your calls, books appointments, and handles customers 24/7 — so you can focus on what you do best.
            </p>
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-xl">
              {[
                { icon: Calendar, label: "Book appointments" },
                { icon: MessageSquare, label: "Answer questions" },
                { icon: Phone, label: "Send SMS reminders" },
                { icon: Users, label: "Capture more customers" },
              ].map(c => (
                <div key={c.label} className="flex flex-col items-start gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-3" data-testid={`chip-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <c.icon className="w-4 h-4 text-blue-400" />
                  <span className="text-xs text-slate-200 leading-snug">{c.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup"><Button data-testid="hero-primary-cta" size="lg" className="rounded-xl bg-blue-600 hover:bg-blue-500 h-12 px-6 text-base shadow-lg shadow-blue-600/30">Get started <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
              <a href="#how"><Button data-testid="hero-secondary-cta" size="lg" variant="outline" className="rounded-xl h-12 px-6 text-base border-white/20 text-white hover:bg-white/10" onClick={(e) => { e.preventDefault(); setDemoOpen(true); }}><Play className="w-4 h-4 mr-2" />Watch demo</Button></a>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Live in minutes</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Setup in minutes</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> Cancel anytime</div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative mx-auto w-[300px] sm:w-[330px]">
              <div className="rounded-[2.75rem] border border-white/15 bg-gradient-to-b from-[#111a30] to-[#0a0f1d] shadow-2xl shadow-blue-900/40 p-6 pt-8" data-testid="hero-phone">
                <div className="text-center">
                  <div className="text-[10px] tracking-[0.2em] text-slate-400 uppercase">ASFA</div>
                  <div className="text-lg font-heading font-semibold mt-0.5">AI Receptionist</div>
                  <div className="text-xs text-slate-400 mt-1 font-mono">00:12</div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-1 h-16">
                  {WAVEFORM.map((h, i) => (
                    <span key={`wf-${i}`} className="wave-bar w-1.5 rounded-full bg-blue-500" style={{ height: `${h * 100}%`, animationDelay: `${i * 0.09}s` }} />
                  ))}
                </div>
                <div className="mt-8 flex items-center justify-center gap-5">
                  <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center"><Mic className="w-4 h-4 text-slate-300" /></div>
                  <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center"><Grid3x3 className="w-4 h-4 text-slate-300" /></div>
                  <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center"><Volume2 className="w-4 h-4 text-slate-300" /></div>
                </div>
                <div className="mt-6 flex justify-center">
                  <div className="w-14 h-14 rounded-full bg-red-500 flex items-center justify-center shadow-lg shadow-red-500/40"><PhoneOff className="w-5 h-5 text-white" /></div>
                </div>
              </div>

              <div className="hidden sm:block absolute -left-44 top-10 w-56 rounded-2xl bg-white text-slate-800 text-xs p-3 shadow-xl float-slow" data-testid="bubble-1">
                {DEMO_SCRIPTS[demoIndustry].lines[0].text}
              </div>
              <div className="hidden sm:block absolute -right-40 top-32 w-52 rounded-2xl bg-blue-600 text-white text-xs p-3 shadow-xl float-slow" style={{ animationDelay: "1.2s" }} data-testid="bubble-2">
                {DEMO_SCRIPTS[demoIndustry].lines[1].text}
              </div>
              <div className="hidden sm:block absolute -left-40 bottom-24 w-60 rounded-2xl bg-white text-slate-800 text-xs p-3 shadow-xl float-slow" style={{ animationDelay: "0.6s" }} data-testid="bubble-3">
                {DEMO_SCRIPTS[demoIndustry].lines[2].text}
              </div>
              <div className="hidden lg:block absolute -right-36 bottom-6 text-blue-300 text-sm rotate-[-4deg]" style={{ fontFamily: "'Caveat', cursive" }}>
                Appointments<br />on autopilot. <span className="inline-block rotate-12">↗</span>
              </div>
              <div className="hidden lg:block absolute -left-32 -top-4 text-blue-300 text-sm rotate-[-6deg]" style={{ fontFamily: "'Caveat', cursive" }}>
                Sounds natural.<br />Because it is.
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-sm font-semibold text-slate-900">Trusted by<br className="sm:hidden" /> businesses like yours</div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
            {INDUSTRIES.map(i => (
              <div key={i.label} className="flex flex-col items-center gap-2 text-slate-400 hover:text-slate-700 transition-colors">
                <i.icon className="w-6 h-6" />
                <span className="text-[10px] font-semibold tracking-widest uppercase">{i.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="industries" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-blue-600">Built for your industry</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">Works for any<br />service-based business</h2>
            <p className="mt-4 text-slate-600 max-w-md leading-relaxed">Whether you run a barbershop, salon, law firm, or detailing business, ASFA adapts to your services, team, and schedule.</p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {INDUSTRIES.map(i => (
                <div key={i.label} className="relative rounded-2xl overflow-hidden h-32 group" data-testid={`industry-${i.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <img src={i.img} alt={i.label} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent" />
                  <div className="absolute bottom-2.5 left-3 flex items-center gap-1.5 text-white text-xs font-semibold">
                    <i.icon className="w-3.5 h-3.5" />{i.label}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={() => setShowAllIndustries(v => !v)} data-testid="view-all-industries-btn" className="inline-block mt-5 text-sm text-blue-600 hover:underline font-medium">
              {showAllIndustries ? "Show fewer industries ↑" : "View all industries →"}
            </button>
            {showAllIndustries && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="all-industries-grid">
                {[
                  "Barbershops", "Salons & Spas", "Dental Clinics", "Medical & Wellness", "Law Firms",
                  "Home Services", "Cleaning Services", "Real Estate", "Restaurants", "Auto Repair",
                  "Car Detailing", "Property Management", "Custom Businesses",
                ].map(name => (
                  <div key={name} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-medium text-slate-700">
                    <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />{name}
                  </div>
                ))}
                <div className="flex items-center gap-2 rounded-xl border border-dashed border-blue-300 bg-blue-50 px-3 py-2.5 text-xs font-medium text-blue-700">
                  <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />…and many more
                </div>
              </div>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm lg:sticky lg:top-24">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">More calls. More bookings. More revenue.</div>
            <h3 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-slate-950">Turn missed calls into paying customers.</h3>
            <div className="mt-7 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0"><Phone className="w-5 h-5 text-emerald-600" /></div>
                <div className="text-3xl font-bold font-heading w-20">67%</div>
                <div className="text-sm text-slate-500">of calls go unanswered by small businesses.</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0"><Calendar className="w-5 h-5 text-blue-600" /></div>
                <div className="text-3xl font-bold font-heading w-20">3x</div>
                <div className="text-sm text-slate-500">more bookings with an AI receptionist.</div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center flex-shrink-0"><Clock className="w-5 h-5 text-violet-600" /></div>
                <div className="text-3xl font-bold font-heading w-20">24/7</div>
                <div className="text-sm text-slate-500">Never miss a customer, even outside business hours.</div>
              </div>
            </div>
            <Link to="/signup"><Button data-testid="industries-cta-btn" className="mt-8 w-full rounded-xl bg-blue-600 hover:bg-blue-700 h-11">Get started <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <p className="mt-3 text-center text-xs text-slate-400">Setup in minutes · Cancel anytime</p>
          </div>
        </div>
      </section>

      <section id="features" className="py-24 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest font-semibold text-blue-600">Everything included</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">Stop missing calls. Start booking more.</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Phone, title: "Answers every call", body: "Even after hours, holidays, or when your hands are full with a customer." },
              { icon: Calendar, title: "Books real appointments", body: "ASFA checks your calendar and confirms the slot instantly." },
              { icon: Bot, title: "Trained on your business", body: "Prices, services, hours, policies — imported from your website in seconds." },
              { icon: Clock, title: "Set up in minutes", body: "Paste your website. Review. Go live. No tech skills needed." },
              { icon: Shield, title: "Human transfer", body: "Escalates to you when it matters — complaints, VIPs, sensitive issues." },
              { icon: Sparkles, title: "ROI on day one", body: "See exactly how many calls, bookings, and dollars ASFA saved you." },
            ].map((f) => (
              <div key={f.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="w-10 h-10 rounded-xl bg-blue-600/10 flex items-center justify-center mb-4"><f.icon className="w-5 h-5 text-blue-600" /></div>
                <div className="font-heading font-semibold text-lg text-slate-950">{f.title}</div>
                <div className="text-sm text-slate-600 mt-2 leading-relaxed">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-blue-600">How it works</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">From website to receptionist in under 5 minutes.</h2>
            <div className="mt-10 space-y-6">
              {[
                { n: "1", t: "Paste your website", d: "ASFA reads it and pulls out your services, hours, prices, and policies." },
                { n: "2", t: "Review & confirm", d: "Everything is editable. Fix anything that doesn't look right." },
                { n: "3", t: "Test your receptionist", d: "Talk to it right in your browser before going live — no phone needed." },
                { n: "4", t: "Go live", d: "Connect your business phone. ASFA starts answering calls." },
              ].map((s) => (
                <div key={s.n} className="flex gap-4">
                  <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold flex-shrink-0 shadow-md shadow-blue-600/30">{s.n}</div>
                  <div>
                    <div className="font-semibold text-slate-950">{s.t}</div>
                    <div className="text-sm text-slate-600 mt-1">{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
            <img src="https://images.unsplash.com/photo-1555529941-66b8c99c4593?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" alt="Front desk of a small business" className="w-full h-[560px] object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      <section id="reviews" className="py-24 bg-[#070b16] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-widest font-semibold text-blue-400">Real businesses. Real results.</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">What business<br />owners are saying</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              { quote: "ASFA books over 80% of our appointments now. It sounds so natural, our clients think it's a real person.", name: "Mike T.", init: "MT" },
              { quote: "We haven't missed a single call since switching to ASFA. It's been a game changer for our salon.", name: "Sarah L.", init: "SL" },
              { quote: "Professional, reliable, and super easy to set up. Highly recommend for any service business.", name: "James R.", init: "JR" },
            ].map(t => (
              <div key={t.name} className="rounded-2xl bg-white/5 border border-white/10 p-6" data-testid={`review-${t.init.toLowerCase()}`}>
                <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={`star-${i}`} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                <p className="mt-4 text-sm text-slate-200 leading-relaxed">"{t.quote}"</p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-sm font-bold">{t.init}</div>
                  <div>
                    <div className="text-sm font-semibold">{t.name}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-24 bg-[#0a0f1d] text-white border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Simple plans that pay for themselves.</h2>
          <p className="mt-4 text-slate-300 text-lg">One-time $100 setup, then monthly. Cancel anytime — used half your minutes or less? You get the month back in full. More than half? Half back.</p>
          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            {[
              { name: "Starter", key: "starter_monthly", price: "$97", bestFor: "For businesses that mainly need call answering and lead capture.", features: ["250 call minutes/mo", "1 phone number · booking + SMS", "FAQs & message taking", "Call transcripts + basic dashboard"] },
              { name: "Business", key: "business_monthly", price: "$225", bestFor: "For businesses that need full appointment management and follow-ups.", features: ["600 call minutes/mo", "Book, cancel & reschedule", "SMS reminders + Google review requests", "Custom business knowledge"], featured: true },
              { name: "Pro", key: "pro_custom", price: "Custom", custom: true, bestFor: "For large businesses with custom needs — let's build your plan together.", features: ["Tailored to your business"] },
            ].map((p) => (
              <div key={p.name} className={`rounded-2xl p-6 text-left ${p.featured ? "bg-blue-600 border-blue-500 shadow-xl shadow-blue-600/30" : "bg-white/5 border-white/10"} border`}>
                <div className="text-sm font-semibold uppercase tracking-wider opacity-80">{p.name}</div>
                <div className="mt-2 text-3xl font-bold">{p.price}{!p.custom && <span className="text-base font-normal opacity-70">/mo</span>}</div>
                <p className={`mt-2 text-xs ${p.featured ? "opacity-100" : "opacity-80"}`} data-testid={`best-for-${p.key}`}>{p.bestFor}</p>
                <ul className="mt-5 space-y-2 text-sm">
                  {p.features.map((f) => <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4" />{f}</li>)}
                </ul>
                {p.custom && (
                  <Button data-testid="pro-contact-btn" variant="outline" onClick={() => setContactOpen(true)}
                    className="mt-6 w-full rounded-xl border-white/30 text-white hover:bg-white/10">Contact us</Button>
                )}
              </div>
            ))}
          </div>
          <Link to="/signup"><Button data-testid="pricing-cta-btn" size="lg" className="mt-10 h-12 px-8 rounded-xl bg-white text-slate-950 hover:bg-slate-100">Get started</Button></Link>
          <p className="mt-5 text-xs text-slate-400">All prices CAD. Overage after included minutes: Starter $0.35/min · Business $0.25/min — you set the spending limit.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-blue-600">Ready to grow?</div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-slate-950">Get your AI receptionist today.</h2>
            <p className="mt-3 text-slate-600 max-w-md">Join hundreds of businesses using ASFA to save time, book more customers, and grow their revenue.</p>
          </div>
          <div className="flex flex-col gap-3 w-full md:w-auto">
            <Link to="/signup"><Button data-testid="footer-cta-btn" size="lg" className="rounded-xl bg-blue-600 hover:bg-blue-700 h-12 px-8 w-full">Get started <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" />One-time $100 setup · then monthly</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" />Setup in minutes</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" />Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      <Dialog open={demoOpen} onOpenChange={setDemoOpen}>
        <DialogContent className="bg-[#0a0f1d] border-white/10 text-white sm:max-w-md" data-testid="demo-video-modal">
          <DialogHeader><DialogTitle className="text-white">Watch ASFA answer a call — in your industry</DialogTitle></DialogHeader>
          <div className="flex flex-wrap gap-1.5" data-testid="demo-industry-picker">
            {DEMO_INDUSTRY_CHOICES.map(c => (
              <button key={c.key} data-testid={`demo-industry-${c.key}`}
                onClick={() => { setDemoIndustry(c.key); setDemoStep(0); setRunKey(k => k + 1); }}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${demoIndustry === c.key ? "bg-blue-600 border-blue-500 text-white" : "border-white/15 text-slate-300 hover:bg-white/10"}`}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="rounded-2xl bg-[#111a30] border border-white/10 p-5">
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span data-testid="demo-biz-name">Incoming call · {DEMO_SCRIPTS[demoIndustry].biz}</span>
              <span className="flex items-center gap-1.5 text-emerald-400"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />Live</span>
            </div>
            <div className="mt-3 flex items-center justify-center gap-1 h-10">
              {WAVEFORM.slice(0, 15).map((h, i) => (
                <span key={`demo-wf-${i}`} className="wave-bar w-1 rounded-full bg-blue-500" style={{ height: `${h * 100}%`, animationDelay: `${i * 0.1}s` }} />
              ))}
            </div>
            <div className="mt-4 space-y-2.5 min-h-[190px]" data-testid="demo-transcript">
              {DEMO_SCRIPTS[demoIndustry].lines.slice(0, demoStep).map((l, i) => (
                <div key={`demo-line-${demoIndustry}-${i}`} className={`text-xs p-2.5 rounded-xl max-w-[85%] leading-relaxed ${l.ai ? "bg-white text-slate-800" : "bg-blue-600 text-white ml-auto"}`}>{l.text}</div>
              ))}
              {demoStep >= DEMO_SCRIPTS[demoIndustry].lines.length && (
                <div className="flex gap-2 pt-1" data-testid="demo-outcomes">
                  <span className="text-[11px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300">✓ Appointment booked</span>
                  <span className="text-[11px] px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300">✓ SMS confirmation sent</span>
                </div>
              )}
              {demoStep >= DEMO_SCRIPTS[demoIndustry].lines.length && (
                <button data-testid="demo-replay-btn" onClick={() => { setDemoStep(0); setRunKey(k => k + 1); }}
                  className="mt-1 text-xs text-blue-300 hover:text-white underline underline-offset-2">↻ Replay demo</button>
              )}
            </div>
          </div>
          <p className="text-xs text-slate-400 text-center">A real conversation — exactly what your callers hear.</p>
          <Link to="/signup"><Button data-testid="demo-modal-cta" className="w-full rounded-xl bg-blue-600 hover:bg-blue-500">Get started</Button></Link>
        </DialogContent>
      </Dialog>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="bg-white text-slate-900 sm:max-w-md" data-testid="contact-dialog">
          <DialogHeader><DialogTitle>Talk to us about a custom plan</DialogTitle></DialogHeader>
          {contactSent ? (
            <div className="py-6 text-center" data-testid="contact-sent">
              <Check className="w-10 h-10 mx-auto text-emerald-500" />
              <p className="mt-3 font-semibold">Message sent</p>
              <p className="text-sm text-slate-500 mt-1">We'll get back to you within one business day.</p>
            </div>
          ) : (
            <form onSubmit={sendContact} className="space-y-3">
              <input data-testid="contact-name" required placeholder="Your name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" />
              <input data-testid="contact-email" required type="email" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" />
              <input data-testid="contact-business" placeholder="Business name" value={contact.business} onChange={(e) => setContact({ ...contact, business: e.target.value })} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm" />
              <textarea data-testid="contact-message" required placeholder="Tell us about your call volume and what you need…" value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm min-h-[96px]" />
              <Button data-testid="contact-submit" type="submit" disabled={contactBusy} className="w-full rounded-xl bg-blue-600 hover:bg-blue-700">{contactBusy ? "Sending…" : "Send"}</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <footer className="py-10 border-t border-slate-200 text-center text-sm text-slate-500">
        © 2026 ASFA. Your business, always answered.
      </footer>
    </div>
  );
}
